// Stage 33 / 29 follow-up: bind-task popover.
//
// Anchored above the panel footer toolbar. Replaces the previous
// centred modal — the modal had no good place to live on small panels
// and obscured the canvas. A panel-attached popover keeps the
// selection list visually tied to the panel it binds, opens upward
// from the footer (so it doesn't overflow the canvas bottom edge), and
// closes on Escape / click-outside / successful selection.
//
// Click-to-bind: each task row is a button that fires the bind
// immediately. No radio + Bind button stage — the popover is the
// selection surface AND the action.

import { useEffect, useRef, useState } from 'react';
import { listUnlinkedTasks, bindPanel } from './bulletinClient';
import type { BulletinPanelDTO, UnlinkedYesterdayTaskDTO } from './types';

export function BindTaskPopover({
  panel,
  onClose,
  onBound,
}: {
  panel: BulletinPanelDTO;
  onClose: () => void;
  onBound: (taskId: string) => void;
}): JSX.Element {
  const [tasks, setTasks] = useState<UnlinkedYesterdayTaskDTO[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void listUnlinkedTasks()
      .then((ts) => {
        setTasks(ts);
        setLoading(false);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
  }, []);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Close on click outside the popover. The footer button that opened
  // it lives outside the popover root, but it toggles its own state
  // directly (open → close on second click), so click-outside hits
  // here only when the user clicks elsewhere on the canvas/page.
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent): void => {
      if (!popoverRef.current) return;
      const target = e.target as Node | null;
      if (target && popoverRef.current.contains(target)) return;
      // Don't close when the user clicks the toggling button itself —
      // it owns the open/close state and would race with the click.
      if (
        target instanceof HTMLElement &&
        target.closest('[data-bulletin-bind-toggle]')
      ) {
        return;
      }
      onClose();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [onClose]);

  const handleSelect = async (taskId: string): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      await bindPanel(panel.id, taskId);
      onBound(taskId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  };

  return (
    <div
      ref={popoverRef}
      className="bulletin-bind-popover"
      role="dialog"
      aria-label={`Bind ${panel.koji} to a Yesterday task`}
    >
      <header className="bulletin-bind-popover-header">
        <span>Bind to Yesterday task</span>
        <button type="button" onClick={onClose} aria-label="Close popover">
          ×
        </button>
      </header>
      <div className="bulletin-bind-popover-body">
        {loading ? (
          <p className="muted small bulletin-bind-popover-empty">Loading…</p>
        ) : tasks.length === 0 ? (
          <p className="muted small bulletin-bind-popover-empty">
            No unlinked Yesterday tasks. Compose one in the Yesterday tab.
          </p>
        ) : (
          <ul className="bulletin-bind-list">
            {tasks.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className="bulletin-bind-task-button"
                  disabled={submitting}
                  onClick={() => void handleSelect(t.id)}
                >
                  <strong>{t.name}</strong>
                  <code className="muted small">
                    {t.koji} · {t.scheduleSummary}
                    {t.status !== 'active' ? ` · ${t.status}` : ''}
                  </code>
                </button>
              </li>
            ))}
          </ul>
        )}
        {error ? <p className="bulletin-error small">⚠ {error}</p> : null}
      </div>
    </div>
  );
}
